import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isMenuFocusMovingOutside } from '../../utils/menuNavigation';
import { FEATURE_PAGES } from './landingContent';
import { BrandMark, ChevronIcon, CloseIcon, MenuIcon } from './LandingIcons';

const DESKTOP_QUERY = '(min-width: 961px)';

const isShown = (element: HTMLElement): boolean => {
  let current: HTMLElement | null = element;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    current = current.parentElement;
  }
  return true;
};

const LandingHeader: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const featureToggleRef = useRef<HTMLButtonElement>(null);
  const featureItemRef = useRef<HTMLLIElement>(null);
  const featureMenuRef = useRef<HTMLDivElement>(null);

  const closeNavigation = useCallback(() => {
    setIsOpen(false);
    setFeaturesOpen(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    closeNavigation();
  }, [closeNavigation, location.pathname, location.hash, location.key]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const desktopQuery = window.matchMedia?.(DESKTOP_QUERY);
    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (event.matches) closeNavigation();
    };

    document.body.style.overflow = 'hidden';
    desktopQuery?.addEventListener('change', handleViewportChange);
    return () => {
      document.body.style.overflow = previousOverflow;
      desktopQuery?.removeEventListener('change', handleViewportChange);
    };
  }, [closeNavigation, isOpen]);

  useEffect(() => {
    // In the open mobile menu the feature list is an inline accordion:
    // collapsing it on pointerdown would move the link being tapped.
    const handlePointerDown = (event: PointerEvent) => {
      if (featuresOpen && !isOpen && !featureItemRef.current?.contains(event.target as Node)) {
        setFeaturesOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && isOpen) {
        // Keep focus inside the open mobile menu.
        const focusable = Array.from(
          headerRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') || [],
        ).filter(isShown);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!headerRef.current?.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
        return;
      }

      if (event.key !== 'Escape') return;
      if (featuresOpen) {
        setFeaturesOpen(false);
        featureToggleRef.current?.focus();
      } else if (isOpen) {
        setIsOpen(false);
        menuToggleRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [featuresOpen, isOpen]);

  const openFeaturesAndFocusFirst = () => {
    setFeaturesOpen(true);
    window.requestAnimationFrame(() => {
      featureMenuRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    });
  };

  const headerClass = [
    'lp-header',
    scrolled ? 'is-scrolled' : '',
    isOpen ? 'is-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <header ref={headerRef} className={headerClass}>
      <div className="lp-wrap lp-header-row">
        <Link to="/" className="lp-brand" onClick={closeNavigation} aria-label="EzyPrep home">
          <BrandMark />
          <span>EzyPrep</span>
        </Link>

        <button
          ref={menuToggleRef}
          type="button"
          className="lp-menu-toggle"
          onClick={() => (isOpen ? closeNavigation() : setIsOpen(true))}
          aria-label={isOpen ? 'Close primary navigation' : 'Open primary navigation'}
          aria-controls="lp-primary-navigation"
          aria-expanded={isOpen}
        >
          {isOpen ? <CloseIcon /> : <MenuIcon />}
        </button>

        <nav
          id="lp-primary-navigation"
          className={`lp-nav${isOpen ? ' is-open' : ''}`}
          aria-label="Primary navigation"
        >
          <ul className="lp-nav-list">
            <li
              ref={featureItemRef}
              className={`lp-nav-item lp-has-menu${featuresOpen ? ' is-active' : ''}`}
              onBlur={(event) => {
                if (!isOpen && isMenuFocusMovingOutside(event.currentTarget, event.relatedTarget)) {
                  setFeaturesOpen(false);
                }
              }}
            >
              <button
                ref={featureToggleRef}
                type="button"
                className="lp-nav-link lp-menu-button"
                aria-expanded={featuresOpen}
                aria-controls="lp-feature-menu"
                onClick={() => setFeaturesOpen(open => !open)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    openFeaturesAndFocusFirst();
                  }
                }}
              >
                Features <ChevronIcon />
              </button>
              <div ref={featureMenuRef} id="lp-feature-menu" className="lp-feature-menu" aria-label="Feature pages">
                <Link to="/#features" className="lp-feature-link" onClick={closeNavigation}>
                  Features Overview
                </Link>
                {FEATURE_PAGES.map(page => (
                  <Link key={page.path} to={page.path} className="lp-feature-link" onClick={closeNavigation}>
                    {page.title}
                  </Link>
                ))}
              </div>
            </li>
            <li className="lp-nav-item">
              <Link to="/#testimonials" className="lp-nav-link" onClick={closeNavigation}>Workflow</Link>
            </li>
            <li className="lp-nav-item">
              <Link to="/#pricing" className="lp-nav-link" onClick={closeNavigation}>Pricing</Link>
            </li>
            <li className="lp-nav-item">
              <Link to="/#faq" className="lp-nav-link" onClick={closeNavigation}>FAQ</Link>
            </li>
          </ul>

          <div className="lp-nav-actions">
            <Link to="/signin" className="lp-nav-link" onClick={closeNavigation}>Sign In</Link>
            <Link to="/try-free" className="lp-btn lp-btn-primary lp-btn-sm" onClick={closeNavigation}>Try for Free</Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default LandingHeader;
